import React, { useState, useEffect, useMemo } from 'react';
import { ManagementOauthApi } from '../../api/apis';
import { apiClient } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useI18n } from '../../contexts/I18nContext';
import { ClientDetailModal } from './ClientDetailModal';
import { Client, ClientWithDevices } from '../../types/client';
import { statusService } from '../../services/statusService';
import { isAbortError } from '../../utils/abortError';

interface ClientsTableProps {
  onClientSelect?: (client: ClientWithDevices) => void;
  refreshTrigger?: number;
}

export const ClientsTable: React.FC<ClientsTableProps> = ({ onClientSelect, refreshTrigger }) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin;
  const { formatDate, t } = useI18n();
  const [clients, setClients] = useState<ClientWithDevices[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sirenSearch, setSirenSearch] = useState('');
  // Replaces the old All/Active/Inactive select. Off by default — only
  // admins can opt in to surface inactive clients.
  const [showInactive, setShowInactive] = useState(false);
  const [sortField, setSortField] = useState<keyof ClientWithDevices>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClient, setSelectedClient] = useState<ClientWithDevices | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [internalRefresh, setInternalRefresh] = useState(0);

  // Initialize API client
  const managementApi = useMemo(() => new ManagementOauthApi(apiClient.getConfiguration()), []);

  // Fetch clients using proper API endpoint
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const ac = new AbortController();
    let cancelled = false;

    const fetchClients = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch clients and system status IN PARALLEL. Previously these
        // were sequential awaits which doubled the Clients page TTFB —
        // /v2/status can be the expensive call at production scale, so
        // overlapping it with the client list is a free win.
        // Note: managementApi (generated client) doesn't take a signal
        // here, so we guard its result with `cancelled`. statusService
        // forwards the signal natively.
        const [rawClients, statusData] = await Promise.all([
          managementApi.apiGetClientListV2V2ClientGet({}),
          statusService.getSystemStatus({ signal: ac.signal }),
        ]);

        if (cancelled) return;

        // Normalize: ensure both name and clientName are populated (retrocompat)
        const clientsData: Client[] = ((rawClients as any[]) || []).map((c: any) => ({
          ...c,
          clientName: c.clientName || c.name || '',
          name: c.clientName || c.name || '',
        }));

        if (!Array.isArray(clientsData)) {
          console.error('[CLIENTS TABLE] Unexpected response format:', clientsData);
          setClients([]);
          return;
        }

        // Create a map of client ID to device stats
        const clientStatsMap = new Map(
          statusData.clients_stats.map(stat => [stat.client_id, stat])
        );

        // Merge client data with device counts from status
        const clientsWithCounts: ClientWithDevices[] = clientsData.map((client: Client) => {
          const stats = clientStatsMap.get(client.id);
          const deviceCount = stats?.total_devices || 0;

          return {
            ...client,
            deviceCount,
            lastActivity: new Date(client.updated_at)
          };
        });

        setClients(clientsWithCounts);
      } catch (err: any) {
        if (cancelled || isAbortError(err)) return;
        console.error('[CLIENTS TABLE] Error fetching clients:', err);
        setError(err.message || 'Failed to fetch clients');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchClients();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [managementApi, isAuthenticated, refreshTrigger, internalRefresh]);

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    let filtered = clients.filter((client) => {
      if (!client?.id || !client?.name) {
        console.warn('Skipping client with missing fields:', client);
        return false;
      }

      // Name search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        client.id.toLowerCase().includes(searchLower) ||
        client.name.toLowerCase().includes(searchLower)
      );

      // SIREN search filter
      const matchesSiren = !sirenSearch ||
        (client.siren && client.siren.includes(sirenSearch));

      // Status filter
      // Default view: active only. Admins see inactive when toggle is on.
      const matchesStatus = (showInactive && canSeeInactive) ? true : !!client.active;

      return matchesSearch && matchesSiren && matchesStatus;
    });

    // Sort clients
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;

      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [clients, searchTerm, sirenSearch, showInactive, canSeeInactive, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedClients.length / itemsPerPage);
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedClients.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedClients, currentPage, itemsPerPage]);

  const handleSort = (field: keyof ClientWithDevices) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleRowClick = (client: ClientWithDevices) => {
    setSelectedClient(client);
    setIsModalOpen(true);
    if (onClientSelect) {
      onClientSelect(client);
    }
  };

  const getSortIcon = (field: keyof ClientWithDevices) => {
    if (field !== sortField) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="clients-table-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading clients...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="clients-table-error">
        <div className="error-container">
          <h3>{t('clients.errorLoading')}</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>{t('errors.retryAction')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="clients-table-container">
      <div className="clients-table-header">
        <div className="table-controls">
          <div className="search-container">
            <input
              type="text"
              data-testid="field-clients-search"
              placeholder={t('clients.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input search-input--ota-style"
              aria-label={t('clients.searchPlaceholder')}
            />
          </div>
          <div className="filter-controls">
            <input
              type="text"
              placeholder={t('clients.searchSirenPlaceholder')}
              value={sirenSearch}
              onChange={(e) => {
                setSirenSearch(e.target.value.replace(/\D/g, ''));
                setCurrentPage(1);
              }}
              className="search-input search-input--ota-style siren-search"
              maxLength={14}
              aria-label={t('clients.searchSirenPlaceholder')}
            />
            {canSeeInactive && (
              <label className="filter-toggle-inactive" title={t('common.showInactiveHint')}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setCurrentPage(1);
                  }}
                />
                {t('common.showInactive')}
              </label>
            )}
          </div>
          <div className="table-info">
            {filteredAndSortedClients.length} clients found
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="clients-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} className="sortable">
                Client Name {getSortIcon('name')}
              </th>
              <th onClick={() => handleSort('deviceCount')} className="sortable">
                Device Count {getSortIcon('deviceCount')}
              </th>
              <th onClick={() => handleSort('siren')} className="sortable">
                SIREN {getSortIcon('siren')}
              </th>
              {showInactive && (
                <th onClick={() => handleSort('active')} className="sortable">
                  Status {getSortIcon('active')}
                </th>
              )}
              <th onClick={() => handleSort('lastActivity')} className="sortable">
                Last Activity {getSortIcon('lastActivity')}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedClients.map((client) => (
              <tr
                key={client.id}
                onClick={() => handleRowClick(client)}
                className="client-row"
              >
                <td className="client-name">{client.name}</td>
                <td className="device-count">
                  <span className="device-count-badge">
                    {client.deviceCount} device{client.deviceCount !== 1 ? 's' : ''}
                  </span>
                </td>
                <td className="client-siren">{client.siren || '-'}</td>
                {showInactive && (
                  <td className="status">
                    <span className={`status-badge ${client.active ? 'active' : 'inactive'}`}>
                      {client.active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </td>
                )}
                <td className="last-activity">
                  {client.lastActivity ? formatDate(client.lastActivity) : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {paginatedClients.length === 0 && !loading && (
          <div className="no-clients">
            <p>No clients found matching your search criteria.</p>
          </div>
        )}
      </div>

      <div className="pagination-wrapper">
        <div className="pagination-info">
          <span>
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedClients.length)} of {filteredAndSortedClients.length} clients
          </span>
          <div className="items-per-page">
            <label htmlFor="clientsItemsPerPage">Items per page:</label>
            <select
              id="clientsItemsPerPage"
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="items-per-page-select"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              First
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>

            <div className="page-numbers">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`page-btn ${page === currentPage ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Last
            </button>
          </div>
        )}
      </div>

      <ClientDetailModal
        client={selectedClient}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedClient(null);
        }}
        onDeviceSelect={() => {}}
        onClientUpdated={() => {
          // Re-fetch the client list without a full page reload
          setInternalRefresh((n) => n + 1);
        }}
      />
    </div>
  );
};
